import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { InfoWindowProps } from '../interface/InfoWindowProps';
import { useNavigate } from 'react-router-dom';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

const urlRegex = /(https?:\/\/[^\s]+)/;

const renderTagValue = (value: unknown) => {
  if (typeof value !== 'string') {
    return String(value);
  }

  const parts = value.split(/(https?:\/\/[^\s]+)/);
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <Link
          key={`link-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#8c195f' }}
        >
          {part}
        </Link>
      );
    }

    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
};

const InfoWindow: React.FC<InfoWindowProps> = ({
  poi,
  isLocalPoi,
  onClose,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const editMode = useSelector((state: RootState) => state.edit.editMode);
  const editInfo = () => {
    navigate('/poi-form', { state: { poi } });
  };
  return (
    <Box
      id={'poi-info-window'}
      sx={{
        position: 'absolute',
        top: 10,
        left: 10,
        p: 2,
        bgcolor: 'white',
        border: '1px solid #ccc',
        borderRadius: '5px',
        maxWidth: isMobile ? '85%' : '400px',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Typography
          sx={{
            mr: 2,
            fontSize: '1.25rem',
            maxWidth: '98%',
            pt: '6px',
            color: 'inherit',
          }}
        >
          {i18n.language === 'de' && poi.category.name_de
            ? `${poi.category.name_de} - ${poi.title}`
            : i18n.language === 'fr' && poi.category.name_fr
              ? `${poi.category.name_fr} - ${poi.title}`
              : i18n.language === 'nl' && poi.category.name_nl
                ? `${poi.category.name_nl} - ${poi.title}`
                : `${poi.category.name} - ${poi.title}`}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#333',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {poi.tags ? (
        <Box>
          {Object.entries(poi.tags).map(([key, value]) => (
            <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
              <strong>{key}:</strong> {renderTagValue(value)}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {poi.description}
        </Typography>
      )}

      {poi.files && poi.files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1, fontSize: '1rem' }}>
            {t('infoWindow.files')}
          </Typography>
          {poi.files.map((file) => (
            <Box
              key={file.id}
              sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  p: 0.5,
                  width: '100%',
                  bgcolor: 'background.default',
                }}
              >
                <InsertDriveFileIcon
                  sx={{ marginRight: 1, color: '#6a6f72' }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {file.name}
                </Typography>
                <IconButton
                  onClick={() => window.open(file.downloadLink, '_blank')}
                  sx={{ marginLeft: 'auto' }}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {isLocalPoi && editMode && (
        <Button
          id={'edit-poi-button'}
          onClick={editInfo}
          variant="contained"
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          {t('infoWindow.edit')}
        </Button>
      )}

      {!isLocalPoi && poi.osm_id && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Link
            href={`https://map.geo.admin.ch/#/map?lang=en&swisssearch=${poi.longitude},${poi.latitude}&z=10&topic=ech&bgLayer=ch.swisstopo.pixelkarte-farbe`}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ color: '#8c195f' }}
          >
            map.geo.admin.ch
          </Link>
          <Link
            href={`https://www.openstreetmap.org/${poi.osm_type}/${poi.osm_id}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ color: '#8c195f' }}
          >
            OpenStreetMap
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default InfoWindow;
